// Operation-aware loading modal controller.
//
// Replaces the copy-pasted per-module `openProgressBar`/`closeProgressBar`
// fake-timer bars (which advanced on wall-clock time, leaked intervals, and
// could present elapsed time as completed work) with ONE shared, accessible,
// indeterminate modal whose state follows the operation it represents.
//
// The single DOM instance lives in templates/partials/loading-modal.html,
// included once from the base layout. This module owns that DOM: its state,
// one active operation "generation", recovery actions, focus restoration, and
// initiating-control locking. There is deliberately NO globally paired
// showLoader()/hideLoader() API — a loader is always scoped to an operation, so
// a late completion from a superseded operation can never hide or overwrite the
// modal a newer operation put up.
//
// Callers use `withLoader(options, operation)`. The lower-level `beginOperation`
// handle is exported for the mechanical caller sweep, where a thin local
// `openProgressBar` wrapper delegates to an owned handle instead of reintroducing
// unowned global show/hide calls. The internal state-transition helpers
// (`showLoading`, `showTimedOut`, `showFailed`, `complete`) are exported for
// focused unit testing.

// ─── Public types ───────────────────────────────────────────────────────────

export type LoadingOperationKind = "read" | "mutation"

export interface LoadingOperationOptions {
  /** Read-only work is cancellation-safe; a mutation may already have reached the server. */
  kind: LoadingOperationKind
  /** Purpose-specific loading copy, e.g. "We are verifying your utility account". */
  message: string
  /** Client-stop-waiting deadline. Defaults to DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number
  /** The control that initiated the work; disabled while active, refocused on close. */
  trigger?: HTMLButtonElement | HTMLInputElement
  /**
   * Whether a failed/timed-out operation may safely be retried. Reads default to
   * retriable; mutations default to NOT retriable (retry requires a documented
   * idempotency/reconciliation contract the client cannot assume).
   */
  canRetry?: boolean
  /** Reconciliation/status action for mutations whose outcome is unknown. */
  onStatus?: () => void
}

export interface LoadingOperationContext {
  /** Aborted when a read operation times out. Pass to every participating fetch. */
  signal: AbortSignal
  /** Client-generated correlation id; also sent as X-Request-ID by callers. */
  requestId: string
  /** Monotonic generation for this operation. */
  generation: number
  /** True only while this operation still owns the modal. */
  isCurrent(): boolean
}

/** Owned handle for the mechanical caller sweep. `withLoader` is built on top of it. */
export interface LoadingOperationHandle {
  readonly context: LoadingOperationContext
  /** Transition out of loading and hide the modal — only if still current and loading. */
  complete(): void
  /** Show the failed state with optional purpose-specific copy — only if still current. */
  fail(message?: string): void
  isCurrent(): boolean
}

export type LoadingModalState = "idle" | "loading" | "timed-out" | "failed"

// ─── Copy ───────────────────────────────────────────────────────────────────

export const DEFAULT_TIMEOUT_MS = 150_000

const LOADING_DETAIL = "Please keep this page open while we finish."
const TIMEOUT_READ_DETAIL = "This is taking longer than expected. No changes were made."
const TIMEOUT_MUTATION_DETAIL = "We stopped waiting, but your request may still be processing."
const FAILED_DETAIL = "We could not complete this request. Please try again."

// ─── DOM contract (see templates/partials/loading-modal.html) ─────────────────

const SEL = {
  root: "[data-ea-loading-modal]",
  overlay: "[data-ea-loading-overlay]",
  dialog: "[data-ea-loading-dialog]",
  title: "[data-ea-loading-title]",
  indicator: "[data-ea-loading-indicator]",
  detail: "[data-ea-loading-detail]",
  retry: "[data-ea-loading-retry]",
  status: "[data-ea-loading-status]",
  close: "[data-ea-loading-close]",
} as const

const REDUCED_MOTION_CLASS = "ea-loading-modal--reduced-motion"
const STATE_ATTR = "data-ea-loading-state"

interface ModalEls {
  root: HTMLElement
  overlay: HTMLElement
  dialog: HTMLElement
  title: HTMLElement
  indicator: HTMLElement
  detail: HTMLElement
  retry: HTMLButtonElement
  status: HTMLButtonElement
  close: HTMLButtonElement
}

function req<T extends Element>(scope: ParentNode, sel: string): T {
  const el = scope.querySelector(sel)
  if (!el) {
    throw new Error(
      `loading-modal: missing element "${sel}". Ensure templates/partials/loading-modal.html is included once in the base layout.`,
    )
  }
  return el as T
}

function getEls(): ModalEls {
  const root = req<HTMLElement>(document, SEL.root)
  return {
    root,
    overlay: req(root, SEL.overlay),
    dialog: req(root, SEL.dialog),
    title: req(root, SEL.title),
    indicator: req(root, SEL.indicator),
    detail: req(root, SEL.detail),
    retry: req<HTMLButtonElement>(root, SEL.retry),
    status: req<HTMLButtonElement>(root, SEL.status),
    close: req<HTMLButtonElement>(root, SEL.close),
  }
}

// ─── Module-level singleton state ─────────────────────────────────────────────

let generationCounter = 0
let activeGeneration = 0
let currentState: LoadingModalState = "idle"
let timeoutTimer: ReturnType<typeof setTimeout> | null = null
let activeController: AbortController | null = null
let activeTrigger: HTMLButtonElement | HTMLInputElement | null = null
let lastFocused: Element | null = null
const busyTriggers = new WeakSet<HTMLElement>()

/** True when `generation` still owns the modal. Stale generations may not touch UI. */
export function isGenerationCurrent(generation: number): boolean {
  return generation === activeGeneration
}

export function getState(): LoadingModalState {
  return currentState
}

function clearTimeoutTimer(): void {
  if (timeoutTimer !== null) {
    clearTimeout(timeoutTimer)
    timeoutTimer = null
  }
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function generateRequestId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto
  if (c && typeof c.randomUUID === "function") return c.randomUUID()
  // Deterministic-shape fallback for environments without crypto.randomUUID.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    const v = ch === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function setActions(els: ModalEls, show: { retry?: boolean; status?: boolean; close?: boolean }): void {
  els.retry.hidden = !show.retry
  els.status.hidden = !show.status
  els.close.hidden = !show.close
}

function focusFirstAvailable(els: ModalEls): void {
  const target = [els.retry, els.status, els.close].find((b) => !b.hidden) ?? els.dialog
  ;(target as HTMLElement).focus()
}

// ─── Focus trap ───────────────────────────────────────────────────────────────
// The modal is blocking (a full-screen overlay sits over the page), so focus must
// stay inside the dialog while it is open. Tab/Shift+Tab cycle the currently
// visible controls; during loading (no controls shown) focus is pinned to the
// dialog.

let keydownHandler: ((e: KeyboardEvent) => void) | null = null

function trapFocus(els: ModalEls): void {
  releaseFocusTrap()
  keydownHandler = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return
    const focusable = [els.retry, els.status, els.close].filter((b) => !b.hidden)
    const active = document.activeElement
    if (focusable.length === 0) {
      e.preventDefault()
      els.dialog.focus()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (active === els.dialog) {
      e.preventDefault()
      ;(e.shiftKey ? last : first).focus()
    } else if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }
  document.addEventListener("keydown", keydownHandler, true)
}

function releaseFocusTrap(): void {
  if (keydownHandler) {
    document.removeEventListener("keydown", keydownHandler, true)
    keydownHandler = null
  }
}

// ─── State transitions (exported for focused unit tests) ──────────────────────

export function showLoading(els: ModalEls, message: string): void {
  currentState = "loading"
  els.root.hidden = false
  els.root.setAttribute(STATE_ATTR, "loading")
  els.overlay.hidden = false
  els.title.textContent = message
  els.detail.textContent = LOADING_DETAIL
  els.indicator.hidden = false
  els.root.classList.toggle(REDUCED_MOTION_CLASS, prefersReducedMotion())
  setActions(els, {})
}

export function showTimedOut(els: ModalEls, kind: LoadingOperationKind, canRetry: boolean, hasStatus: boolean): void {
  currentState = "timed-out"
  els.root.setAttribute(STATE_ATTR, "timed-out")
  els.indicator.hidden = true
  if (kind === "read") {
    els.detail.textContent = TIMEOUT_READ_DETAIL
    setActions(els, { retry: canRetry, close: true })
  } else {
    // Mutation: never claim cancellation. Offer reconciliation when available and
    // keep resubmit locked; when no reconciliation handler is wired (e.g. the
    // mechanical caller sweep), fall back to a Close so a genuine hang is never a
    // permanently stuck modal.
    els.detail.textContent = TIMEOUT_MUTATION_DETAIL
    setActions(els, { status: hasStatus, close: !hasStatus })
  }
  focusFirstAvailable(els)
}

export function showFailed(els: ModalEls, canRetry: boolean, hasStatus: boolean, message?: string): void {
  currentState = "failed"
  els.root.setAttribute(STATE_ATTR, "failed")
  els.indicator.hidden = true
  els.detail.textContent = message ?? FAILED_DETAIL
  // Retry only when read or explicitly idempotent (canRetry); otherwise offer a
  // reconciliation/status action instead.
  setActions(els, { retry: canRetry, status: hasStatus && !canRetry, close: true })
  focusFirstAvailable(els)
}

export function complete(els: ModalEls): void {
  currentState = "idle"
  clearTimeoutTimer()
  els.indicator.hidden = true
  els.root.hidden = true
  els.overlay.hidden = true
  els.root.setAttribute(STATE_ATTR, "idle")
  setActions(els, {})
}

// ─── Teardown / recovery ──────────────────────────────────────────────────────

function reset(): void {
  const els = getEls()
  releaseFocusTrap()
  complete(els)
  activeController = null
  const trigger = activeTrigger
  activeTrigger = null
  if (trigger) {
    busyTriggers.delete(trigger)
    trigger.disabled = false
  }
  // Invalidate any in-flight generation so late completions are ignored.
  activeGeneration = 0
  const toFocus = (trigger ?? lastFocused) as HTMLElement | null
  lastFocused = null
  toFocus?.focus?.()
}

// ─── Operation lifecycle ──────────────────────────────────────────────────────

/**
 * Begin an owned operation. Returns null if the same trigger already owns an
 * in-flight operation (repeat activation is rejected rather than double-firing).
 */
export function beginOperation(options: LoadingOperationOptions): LoadingOperationHandle | null {
  const trigger = options.trigger ?? null
  if (trigger && busyTriggers.has(trigger)) return null

  const els = getEls()
  const generation = ++generationCounter
  activeGeneration = generation

  const controller = new AbortController()
  activeController = controller
  activeTrigger = trigger
  lastFocused = document.activeElement
  const requestId = generateRequestId()
  const canRetry = options.canRetry ?? options.kind === "read"
  const hasStatus = typeof options.onStatus === "function"

  if (trigger) {
    busyTriggers.add(trigger)
    trigger.disabled = true
  }

  showLoading(els, options.message)
  focusFirstAvailable(els) // dialog receives focus while no actions are shown
  trapFocus(els) // keep Tab inside the blocking dialog until it closes

  const isCurrent = () => generation === activeGeneration

  // Wire recovery actions for THIS generation only.
  els.close.onclick = () => {
    if (isCurrent()) reset()
  }
  els.status.onclick = () => {
    if (isCurrent()) options.onStatus?.()
  }
  // retry.onclick is (re)assigned by withLoader when it enters a retriable state.

  clearTimeoutTimer()
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  timeoutTimer = setTimeout(() => {
    if (!isCurrent()) return
    if (options.kind === "read") {
      // Cancellation-safe: abort participating fetches, then show timed-out.
      controller.abort()
    }
    showTimedOut(els, options.kind, canRetry, hasStatus)
  }, timeoutMs)

  const context: LoadingOperationContext = {
    signal: controller.signal,
    requestId,
    generation,
    isCurrent,
  }

  return {
    context,
    complete() {
      if (isCurrent() && currentState === "loading") reset()
    },
    fail(message?: string) {
      if (isCurrent()) showFailed(getEls(), canRetry, hasStatus, message)
    },
    isCurrent,
  }
}

/**
 * Run `operation` under the shared loading modal. Resolves/rejects with the
 * operation's result. On timeout the modal shows a kind-appropriate recovery
 * state; a completion from a superseded generation is ignored for UI purposes.
 */
export function withLoader<T>(
  options: LoadingOperationOptions,
  operation: (context: LoadingOperationContext) => Promise<T>,
): Promise<T> {
  const handle = beginOperation(options)
  if (!handle) {
    return Promise.reject(new Error("loading-modal: an operation is already active for this trigger"))
  }

  const canRetry = options.canRetry ?? options.kind === "read"
  if (canRetry) {
    // Retry starts a fresh generation via a new withLoader call.
    const els = getEls()
    els.retry.onclick = () => {
      if (handle.isCurrent() && (currentState === "timed-out" || currentState === "failed")) {
        void withLoader(options, operation)
      }
    }
  }

  return operation(handle.context).then(
    (result) => {
      handle.complete()
      return result
    },
    (error) => {
      // A superseded generation may run its own cleanup but must not touch the modal.
      if (handle.isCurrent() && !handle.context.signal.aborted) {
        handle.fail()
      }
      throw error
    },
  )
}

/** Test-only: restore module singleton state between cases. */
export function _resetForTests(): void {
  clearTimeoutTimer()
  releaseFocusTrap()
  generationCounter = 0
  activeGeneration = 0
  currentState = "idle"
  activeController = null
  activeTrigger = null
  lastFocused = null
}

export { getEls as _getEls }
export type { ModalEls as _ModalEls }
